import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../Card';
import FunFact from '../FunFact';
import Hero from '../Hero';
import Div from '../Div';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';
import Cta from '../Cta';
import MovingText from '../MovingText';
import PortfolioSlider from '../Slider/PortfolioSlider';
import { bgImageData } from '../../data/bgImageData.js';
import { portfolioData } from '../../data/portfolioData.js';
import { getServicesData } from '../../data/servicesData.js';
import { pageTitle } from '../../helper';
import { updateMetaTags } from '../../helper/updateMetaTags';
import { Helmet } from 'react-helmet';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import PricingTableList from '../PricingTable/PricingTableList.jsx';
import { localizePath } from '../../helper/localizedPath';

const heroSocialLinks = [
  {
    name: 'Facebook',
    links: 'https://www.facebook.com/Stablerecordscz/',
  },
  {
    name: 'Instagram',
    links: 'https://www.instagram.com/stablerecords_cz/',
  },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'en' ? 'en' : 'cs';
  const servicesData = getServicesData(i18n.language === 'en' ? 'en' : 'cs');

  pageTitle('Home');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const translatedFunFacts = [
    { title: t('funFacts.releasedSingles'), factNumber: '50' },
    { title: t('funFacts.sqmSpace'), factNumber: '30' },
    { title: t('funFacts.proEquipment'), factNumber: '40' },
    { title: t('funFacts.ampsEffects'), factNumber: '15' }
  ];

  return (
    <>
    <Helmet>
      <title>{t('home.metaTitle')}</title>
      <link rel="icon" href="/stablerecords.ico" />
      <meta name="description" content={t('home.metaDescription')} />
    </Helmet>
      <div>
        {updateMetaTags(t('home.ogDescription'), t('home.ogKeywords'))}
      </div>
      <Hero
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        text={t('hero.text')}
        btnText={t('hero.btnText')}
        btnLink={'/kontakt'}
        scrollDownId="#service"
        socialLinksHeading={t('hero.followUs')}
        heroSocialLinks={heroSocialLinks}
        bgImageUrl={bgImageData}
        overlay={true}
      />

      <div className="container">
        <FunFact
          variant="cs-type1"
          title={t('home.funFactTitle')}
          subtitle={t('home.funFactSubtitle')}
          data={translatedFunFacts}
        />
      </div>

      <LazyLoadComponent>
      <Spacing lg="150" md="80" />
      <Div id="service">
        <Div className="container">
          <h2 className="cs-font_38 cs-m0">
            {t('home.localStudioHeading')}
          </h2>
          <Spacing lg="20" md="15" />
          <p className="cs-m0">
            {t('home.localStudioIntroStart')} <Link to={localizePath('/sluzby/nahravani', language)}>{t('home.localStudioAnchor')}</Link>{t('home.localStudioIntroEnd')}
          </p>
          <Spacing lg="45" md="30" />
          <Div className="row">
            <Div className="col-xl-4">
              <SectionHeading
                title={t('home.servicesTitle')}
                subtitle={t('home.servicesSubtitle')}
                btnText={t('home.viewAllServices')}
                btnLink={'/sluzby'}
              />
              <Spacing lg="90" md="45" />
            </Div>
            <Div className="col-xl-8">
              <Div className="row">
                <Div className="col-lg-3 col-sm-6 cs-hidden_mobile"></Div>
                <Div className="col-lg-3 col-sm-6">
                  <Card
                    title={servicesData[2].name}
                    link={"/sluzby/" + servicesData[2].id}
                    src={servicesData[2].src}
                    alt="Service"
                  />
                  <Spacing lg="0" md="30" />
                </Div>
                <Div className="col-lg-3 col-sm-6 cs-hidden_mobile"></Div>
                <Div className="col-lg-3 col-sm-6">
                  <Card
                    title={servicesData[1].name}
                    link={"/sluzby/" + servicesData[1].id}
                    src={servicesData[1].src}
                    alt="Service"
                  />
                  <Spacing lg="0" md="30" />
                </Div>
                <Div className="col-lg-3 col-sm-6">
                  <Card
                    title={t('home.studioRental')}
                    link={"/sluzby/" + servicesData[3].id}
                    src={servicesData[3].src}
                    alt="Service"
                  />
                  <Spacing lg="0" md="30" />
                </Div>
                <Div className="col-lg-3 col-sm-6 cs-hidden_mobile"></Div>
                <Div className="col-lg-3 col-sm-6">
                  <Card
                    title={t('home.accommodation')}
                    link={"/sluzby/" + servicesData[0].id}
                    src={servicesData[0].src}
                    alt="Service"
                  />
                  <Spacing lg="0" md="30" />
                </Div>
                <Div className="col-lg-3 col-sm-6 cs-hidden_mobile"></Div>
              </Div>
            </Div>
          </Div>
        </Div>
      </Div>
      </LazyLoadComponent>

      <LazyLoadComponent>
      <Spacing lg="150" md="50" />
      <Div>
        <Div className="container">
          <SectionHeading
            title={t('home.portfolioTitle')}
            subtitle={t('home.portfolioSubtitle')}
            variant="cs-style1 text-center"
          />
          <Spacing lg="90" md="45" />
        </Div>
        <PortfolioSlider data={portfolioData} />
      </Div>
      </LazyLoadComponent>

      <Spacing lg="150" md="80" />
      <MovingText text="Stable records studios Stable records studios " />
      <Spacing lg="105" md="70" />
      <div id="cenik">
      <Spacing lg='150' md='80'/>
      <Div className="container">
        <SectionHeading
          title={t('home.pricingTitle')}
          subtitle={t('home.pricingSubtitle')}
        />
        <Spacing lg='85' md='40'/>
          <PricingTableList />
      </Div>
      </div>

      <Div className="container">
        <Cta
          title={t('home.ctaTitle')}
          btnText={t('home.ctaBtn')}
          btnLink={'/kontakt'}
          bgSrc="/images/cta_bg.jpeg"
        />
      </Div>
    </>
  );
}